import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { DPUser } from '../../core/models/DPUser';

/**
 * Service to manage user context for Sentry error tracking
 */
@Injectable({
  providedIn: 'root',
})
export class SentryUserService {
  /**
   * Set user context in Sentry for error tracking
   */
  setUser(user: DPUser | null): void {
    if (typeof Sentry === 'undefined' || !Sentry.setUser) {
      return; // Sentry not available
    }

    if (!user) {
      // Clear user context on logout
      Sentry.setUser(null);
      // Clear organization context as well
      if (typeof Sentry !== 'undefined' && Sentry.setContext) {
        Sentry.setContext('organization', null);
      }
      return;
    }

    // Set comprehensive user context
    Sentry.setUser({
      id: user.UserID || user.DriverID?.toString(),
      email: user.Email || user.UserName,
      username: user.UserName,
      // Additional context for better debugging
      extra: {
        driverId: user.DriverID,
        driverStatus: user.DriverStatus,
        companyId: user.CompanyID,
        tenant: user.Tenant,
        hasAccessedDashboard: user.HasAccessedDashboard,
        hireDate: user.HireDate,
      },
    });
  }

  /**
   * Add additional context to current error reports
   */
  setContext(key: string, context: any): void {
    if (typeof Sentry !== 'undefined' && Sentry.setContext) {
      Sentry.setContext(key, context);
    }
  }

  /**
   * Add tags for better error filtering
   */
  setTag(key: string, value: string): void {
    if (typeof Sentry !== 'undefined' && Sentry.setTag) {
      Sentry.setTag(key, value);
    }
  }

  /**
   * Add a breadcrumb for user actions to track the sequence of events leading to an error
   * @param category - Type of action (e.g., 'navigation', 'form', 'auth', 'api')
   * @param message - Description of the action
   * @param level - Severity level of the breadcrumb
   * @param data - Optional additional data
   */
  addBreadcrumb(
    category: string,
    message: string,
    level: 'info' | 'warning' | 'error' = 'info',
    data?: Record<string, any>
  ): void {
    if (typeof Sentry !== 'undefined' && Sentry.addBreadcrumb) {
      Sentry.addBreadcrumb({
        category,
        message,
        level,
        timestamp: Date.now() / 1000,
        data,
      });
    }
  }

  /**
   * Set organizational context for multi-tenant tracking
   * @param companyId - The company/organization ID
   * @param tenant - The tenant identifier
   * @param additionalData - Any additional organizational data
   */
  setOrganizationContext(
    companyId: number,
    tenant: string,
    additionalData?: Record<string, any>
  ): void {
    if (typeof Sentry !== 'undefined' && Sentry.setContext) {
      Sentry.setContext('organization', {
        companyId,
        tenant,
        timestamp: new Date().toISOString(),
        ...additionalData,
      });
    }
  }
}
